import {
  Activity,
  BellRing,
  Cog,
  FlaskConical,
  Gift,
  History,
  Bot,
  BotMessageSquare,
  CalendarDays,
  ChartNoAxesCombined,
  Check,
  ChevronDown,
  ClipboardList,
  DoorOpen,
  Handshake,
  GraduationCap,
  LayoutDashboard,
  Link2,
  PhoneCall,
  Headset,
  FileInput,
  Wallet,
  MapPinned,
  ChartColumnBig,
  Megaphone,
  MessageSquare,
  MessageSquareQuote,
  MessageSquareText,
  Network,
  PanelLeftClose,
  Radio,
  Send,
  Unlink,
  Settings2,
  ShieldCheck,
  Sparkles,
  UserRoundCheck,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import { recoverWorkspaceSnapshot, useApp } from "../lib/app-context";
import { canManageTeamRole, teamRoleLabel } from "../lib/team-access";
import { useNativeCalendar } from "../lib/native-calendar-context";
import { fetchOwnTelegramLink, unlinkOwnTelegram } from "../lib/telegram-api";
import type { TelegramLink } from "@vekha/contracts";
import {
  ALL_ACTIVE_AGENTS,
  ALL_ACTIVE_SCENARIOS,
  useWorkContext,
  workModeLabel,
} from "../lib/work-context";
import { Brand } from "./brand";
import { SECTION_CATALOG, sectionEntryPath, type SectionIcon } from "@vekha/contracts";
import { Button, Notice } from "./ui";

type NavigationItem = Readonly<{
  to: string;
  label: string;
  icon: LucideIcon;
  activePaths: readonly string[];
  /** Shown only when the native calendar that owns these messages is enabled. */
  calendarOnly?: boolean;
}>;

const sectionIcons: Record<SectionIcon, LucideIcon> = {
  dialogs: MessageSquare, campaigns: Megaphone, webinars: Radio, calendar: CalendarDays,
  gift: Gift, agents: Bot, scenarios: Network, auto_messages: MessageSquareText,
  playground: FlaskConical, connections: Link2, action_log: ClipboardList, status: Activity,
  alerts: BellRing, promises: ShieldCheck, robots: Cog, bots: BotMessageSquare,
  where: MapPinned, access: UserRoundCheck, analytics: ChartNoAxesCombined,
  summary: LayoutDashboard, ads: Megaphone, sales: Handshake, lidorub: PhoneCall, money: Wallet, entries: DoorOpen,
  consultations: Headset, practicum: GraduationCap, voice: MessageSquareQuote, marketing: ChartColumnBig, forms: FileInput,
};
const UNSCOPED_SECTIONS: readonly string[] = SECTION_CATALOG.filter(section => section.unscoped).map(section => section.path);

function NavigationLink({ item, search, activeTo }: { item: NavigationItem; search: string; activeTo: string | null }) {
  const Icon = item.icon;
  const { pathname } = useLocation();
  const active = activeTo === null ? navigationItemActive(item, pathname) : activeTo === item.to;
  return (
    <NavLink
      to={{ pathname: item.to, search: UNSCOPED_SECTIONS.includes(item.to) ? "" : search }}
      className={`nav-link ${active ? "nav-link--active" : ""}`}
      aria-current={active ? "page" : undefined}
      title={item.label}
    >
      <Icon aria-hidden="true" size={19} strokeWidth={1.8} />
      <span>{item.label}</span>
    </NavLink>
  );
}
/** The entry whose path matches the page most precisely: «Аналитика» owns /analytics, «Эфиры» its report /analytics/webinars. */
function activeNavigation(items: readonly NavigationItem[], pathname: string): string | null {
  let best: { to: string; length: number } | null = null;
  for (const item of items) for (const path of item.activePaths) {
    if ((pathname === path || pathname.startsWith(`${path}/`)) && (!best || path.length > best.length)) best = { to: item.to, length: path.length };
  }
  return best?.to ?? null;
}
function navigationItemActive(item: NavigationItem, pathname: string): boolean {
  return item.activePaths.some(path => pathname === path || pathname.startsWith(`${path}/`));
}

function NavigationGroup({ label, items, search, collapsed, activeTo }: {
  activeTo: string | null;
  label: string;
  items: readonly NavigationItem[];
  search: string;
  collapsed: boolean;
}) {
  const [expanded, setExpanded] = useState(() => {
    try { return localStorage.getItem(`vekha.navigation.${label}`) !== "collapsed"; } catch { return true; }
  });
  if (items.length === 0) return null;
  return (
    <div className="sidebar__group">
      <button type="button" className="sidebar__section-label sidebar__section-label--toggle" aria-expanded={expanded} onClick={() => {
        setExpanded(value => !value);
        try { localStorage.setItem(`vekha.navigation.${label}`, expanded ? "collapsed" : "expanded"); } catch { /* Navigation works without storage. */ }
      }}>{label}<ChevronDown size={14} aria-hidden="true" /></button>
      {expanded || collapsed ? <nav className="sidebar__nav" aria-label={label}>
        {items.map((item) => <NavigationLink key={item.to} item={item} search={search} activeTo={activeTo} />)}
      </nav> : null}
    </div>
  );
}

function russianCount(count: number, one: string, few: string, many: string): string {
  const modulo100 = Math.abs(count) % 100;
  const modulo10 = modulo100 % 10;
  const noun = modulo100 >= 11 && modulo100 <= 19
    ? many
    : modulo10 === 1
      ? one
      : modulo10 >= 2 && modulo10 <= 4
        ? few
        : many;
  return `${count} ${noun}`;
}

function AgentContextPicker({
  agentId,
  agents,
  onChange,
}: {
  agentId: string;
  agents: ReturnType<typeof useWorkContext>["agents"];
  onChange: (agentId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const selected = agentId === ALL_ACTIVE_AGENTS
    ? null
    : agents.find((agent) => agent.id === agentId) ?? agents[0] ?? null;

  useEffect(() => {
    if (!open) return undefined;
    function closeOnOutsidePointer(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("pointerdown", closeOnOutsidePointer);
    queueMicrotask(() => {
      rootRef.current?.querySelector<HTMLButtonElement>(
        '.work-context-agent-option[aria-selected="true"]',
      )?.focus();
    });
    return () => document.removeEventListener("pointerdown", closeOnOutsidePointer);
  }, [open]);

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      setOpen(false);
      rootRef.current?.querySelector<HTMLButtonElement>(".work-context-agent-trigger")?.focus();
      return;
    }
    if (!open && ["ArrowDown", "ArrowUp"].includes(event.key)) {
      event.preventDefault();
      setOpen(true);
      return;
    }
    if (open && ["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      const options = [...(rootRef.current?.querySelectorAll<HTMLButtonElement>(
        ".work-context-agent-option",
      ) ?? [])];
      if (options.length === 0) return;
      event.preventDefault();
      const currentIndex = options.findIndex((option) => option === document.activeElement);
      const nextIndex = event.key === "Home"
        ? 0
        : event.key === "End"
          ? options.length - 1
          : event.key === "ArrowUp"
            ? (currentIndex <= 0 ? options.length - 1 : currentIndex - 1)
            : (currentIndex + 1) % options.length;
      options[nextIndex]?.focus();
    }
  }

  function select(nextAgentId: string) {
    setOpen(false);
    onChange(nextAgentId);
  }

  return (
    <div className="work-context-agent-picker" ref={rootRef} onKeyDown={handleKeyDown}>
      <span className="work-context-agent-label">Агент:</span>
      <div className="work-context-agent-control">
        <button
          type="button"
          className="work-context-agent-trigger"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls="work-context-agent-listbox"
          onClick={() => setOpen((value) => !value)}
        >
          {agentId === ALL_ACTIVE_AGENTS ? (
            <span className="work-context-agent-avatar" aria-hidden="true">Все</span>
          ) : selected?.operatorRuntime?.avatarUrl ? (
            <img src={selected.operatorRuntime.avatarUrl} alt="" />
          ) : (
            <span className="work-context-agent-avatar" aria-hidden="true">
              {selected?.name.slice(0, 1).toUpperCase() ?? "—"}
            </span>
          )}
          <span className="work-context-agent-trigger__name">{agentId === ALL_ACTIVE_AGENTS ? "Все агенты" : selected?.name ?? "Выберите агента"}</span>
          <ChevronDown aria-hidden="true" size={15} />
        </button>
        {open ? (
          <div
            id="work-context-agent-listbox"
            className="work-context-agent-menu"
            role="listbox"
            aria-label="Агенты"
          >
            <button
              type="button"
              className="work-context-agent-option"
              role="option"
              aria-selected={agentId === ALL_ACTIVE_AGENTS}
              onClick={() => select(ALL_ACTIVE_AGENTS)}
            >
              <span className="work-context-agent-avatar" aria-hidden="true">Все</span>
              <span className="work-context-agent-option__main">
                <strong>Все агенты</strong>
                <small>Общая очередь сотрудников</small>
              </span>
              {agentId === ALL_ACTIVE_AGENTS ? <Check aria-hidden="true" size={17} /> : null}
            </button>
            {agents.map((agent) => (
              <button
                key={agent.id}
                type="button"
                className="work-context-agent-option"
                role="option"
                aria-selected={agent.id === agentId}
                onClick={() => select(agent.id)}
              >
                {agent.operatorRuntime?.avatarUrl ? (
                  <img src={agent.operatorRuntime.avatarUrl} alt="" />
                ) : (
                  <span className="work-context-agent-avatar" aria-hidden="true">
                    {agent.name.slice(0, 1).toUpperCase()}
                  </span>
                )}
                <span className="work-context-agent-option__main">
                  <strong>{agent.name}</strong>
                  <small>{agent.role}</small>
                </span>
                <span className={`work-context-agent-status ${agent.status === "active" ? "is-active" : ""}`}>
                  {agent.status === "active" ? "Активна" : "Черновик"}
                </span>
                {agent.id === agentId ? <Check aria-hidden="true" size={17} /> : null}
              </button>
            ))}
            {agents.length === 0 ? (
              <div className="work-context-scenario-empty">Опубликованных агентов пока нет.</div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ScenarioContextPicker({
  scenarioId,
  activeScenarios,
  allDialogCount,
  onChange,
}: {
  scenarioId: string;
  activeScenarios: ReturnType<typeof useWorkContext>["activeScenarios"];
  allDialogCount: number;
  onChange: (scenarioId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const selected = activeScenarios.find((scenario) => scenario.id === scenarioId) ?? null;
  const selectedLabel = scenarioId === ALL_ACTIVE_SCENARIOS
    ? "Все активные сценарии"
    : selected?.name ?? "Выберите сценарий";

  useEffect(() => {
    if (!open) return undefined;
    function closeOnOutsidePointer(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("pointerdown", closeOnOutsidePointer);
    queueMicrotask(() => {
      const selectedOption = rootRef.current?.querySelector<HTMLButtonElement>(
        '.work-context-scenario-option[aria-selected="true"]',
      );
      selectedOption?.focus();
    });
    return () => document.removeEventListener("pointerdown", closeOnOutsidePointer);
  }, [open]);

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      setOpen(false);
      rootRef.current?.querySelector<HTMLButtonElement>(".work-context-scenario-trigger")?.focus();
      return;
    }
    if (open && ["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      const options = [...(rootRef.current?.querySelectorAll<HTMLButtonElement>(
        ".work-context-scenario-option",
      ) ?? [])];
      if (options.length === 0) return;
      event.preventDefault();
      const currentIndex = options.findIndex((option) => option === document.activeElement);
      const nextIndex = event.key === "Home"
        ? 0
        : event.key === "End"
          ? options.length - 1
          : event.key === "ArrowUp"
            ? (currentIndex <= 0 ? options.length - 1 : currentIndex - 1)
            : (currentIndex + 1) % options.length;
      options[nextIndex]?.focus();
    }
  }

  function select(nextScenarioId: string) {
    setOpen(false);
    onChange(nextScenarioId);
  }

  return (
    <div className="work-context-scenario-picker" ref={rootRef} onKeyDown={handleKeyDown}>
      <span className="work-context-scenario-label">Сценарий:</span>
      <div className="work-context-scenario-control">
        <button
          type="button"
          className="work-context-scenario-trigger"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls="work-context-scenario-listbox"
          onClick={() => setOpen((value) => !value)}
        >
          <span className="work-context-scenario-trigger__name">{selectedLabel}</span>
          <span className="work-context-scenario-trigger__count">{russianCount(activeScenarios.length, "активный", "активных", "активных")}</span>
          <ChevronDown aria-hidden="true" size={15} />
        </button>
        {open ? (
          <div
            id="work-context-scenario-listbox"
            className="work-context-scenario-menu"
            role="listbox"
            aria-label="Сценарии выбранного агента"
          >
            <button
              type="button"
              className="work-context-scenario-option"
              role="option"
              aria-selected={scenarioId === ALL_ACTIVE_SCENARIOS}
              onClick={() => select(ALL_ACTIVE_SCENARIOS)}
            >
              <span className="work-context-scenario-option__main">
                <strong>Все активные сценарии</strong>
                <small>Общая очередь агента</small>
              </span>
              <span className="work-context-scenario-option__meta">
                <span>{russianCount(allDialogCount, "диалог", "диалога", "диалогов")}</span>
                {scenarioId === ALL_ACTIVE_SCENARIOS ? <Check aria-hidden="true" size={17} /> : null}
              </span>
            </button>
            {activeScenarios.map((scenario) => {
              return (
                <button
                  key={scenario.id}
                  type="button"
                  className="work-context-scenario-option"
                  role="option"
                  aria-selected={scenario.id === scenarioId}
                  onClick={() => select(scenario.id)}
                >
                  <span className="work-context-scenario-option__main">
                    <strong>{scenario.name}</strong>
                    <small>{scenario.primaryEmployeeName}</small>
                  </span>
                  <span className="work-context-scenario-option__meta">
                    <span>{russianCount(scenario.dialogCount, "диалог", "диалога", "диалогов")}</span>
                    <small>Активен · {workModeLabel(scenario.operatingState.mode)}</small>
                    {scenario.id === scenarioId ? <Check aria-hidden="true" size={17} /> : null}
                  </span>
                </button>
              );
            })}
            {activeScenarios.length === 0 ? (
              <div className="work-context-scenario-empty">У агента пока нет активных сценариев.</div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The agent, the scenario and its mode. «Диалоги» show it compact over the queue («Спираль», 05.11.2026: chips «Яна ▾ ·
 * сценарий ▾ · ◆ режим ▾»), other screens in the top bar.
 */
export function WorkContextBar({ compact = false }: { compact?: boolean }) {
  const [modeOpen, setModeOpen] = useState(false);
  const modeRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!modeOpen) return;
    const close = (event: MouseEvent) => { if (!modeRef.current?.contains(event.target as Node)) setModeOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [modeOpen]);
  const {
    agentId,
    scenarioId,
    agents,
    activeScenarios,
    selectedScenario,
    setAgentId,
    setScenarioId,
    setMode,
    modePending,
    modeError,
    modeNotice,
    contextNotice,
  } = useWorkContext();
  const currentMode = selectedScenario?.operatingState.mode ?? "off";
  const allDialogCount = activeScenarios.reduce((sum, scenario) => sum + scenario.dialogCount, 0);
  const canChangeMode = scenarioId !== ALL_ACTIVE_SCENARIOS
    && selectedScenario?.activeActivation !== null
    && selectedScenario !== null;
  const modes = [
    { value: "off", label: "Выключен" },
    { value: "drafts", label: "Черновики" },
    { value: "live", label: "Автопилот" },
  ] as const;

  const currentLabel = modes.find((mode) => mode.value === (currentMode === "preview" ? "drafts" : currentMode))?.label ?? "Выключен";
  return (
    <>
      <div className={`work-context-bar${compact ? " work-context-bar--compact" : ""}`} aria-label="Текущий рабочий контекст">
        <AgentContextPicker agentId={agentId} agents={agents} onChange={setAgentId} />
        <span className="work-context-dot" aria-hidden="true">·</span>
        <ScenarioContextPicker
          scenarioId={scenarioId}
          activeScenarios={activeScenarios}
          allDialogCount={allDialogCount}
          onChange={setScenarioId}
        />
        {compact ? (
          <div className="work-context-mode-chip" ref={modeRef}>
            <button
              type="button"
              className={`work-context-mode-chip__trigger work-context-mode-chip__trigger--${currentMode === "preview" ? "drafts" : currentMode}`}
              aria-haspopup="true"
              aria-expanded={modeOpen}
              aria-label={`Режим сценария: ${currentLabel}`}
              title={canChangeMode ? "Режим выбранного сценария" : "Выберите один сценарий, чтобы изменить его режим."}
              onClick={() => setModeOpen((value) => !value)}
            >
              <i aria-hidden="true" />{currentLabel}<ChevronDown aria-hidden="true" size={13} />
            </button>
            {modeOpen ? (
              <div className="work-context-mode-chip__menu work-context-modes" aria-label="Режим сценария">
                {modes.map((mode) => {
                  const active = mode.value === "drafts"
                    ? currentMode === "drafts" || currentMode === "preview"
                    : currentMode === mode.value;
                  return (
                    <button
                      key={mode.value}
                      type="button"
                      className={`work-context-mode-button${active ? " is-active" : ""}`}
                      aria-pressed={active}
                      disabled={!canChangeMode || modePending}
                      onClick={() => { setModeOpen(false); void setMode(mode.value); }}
                    >
                      {mode.label}{active ? <Check aria-hidden="true" size={15} /> : null}
                    </button>
                  );
                })}
                {canChangeMode ? null : <small>Выберите один сценарий, чтобы изменить его режим.</small>}
              </div>
            ) : null}
          </div>
        ) : <div
          className="work-context-modes"
          aria-label="Режим сценария"
          title={canChangeMode ? "Режим выбранного сценария" : "Выберите один сценарий, чтобы изменить его режим."}
        >
          {modes.map((mode) => {
            const active = mode.value === "drafts"
              ? currentMode === "drafts" || currentMode === "preview"
              : currentMode === mode.value;
            return (
              <button
                key={mode.value}
                type="button"
                className={`work-context-mode-button${active ? " is-active" : ""}`}
                aria-pressed={active}
                disabled={!canChangeMode || modePending}
                onClick={() => void setMode(mode.value)}
              >
                {mode.label}
              </button>
            );
          })}
        </div>}
      </div>
      {contextNotice ? <div className="work-context-selection-notice" role="alert">{contextNotice}</div> : null}
      {modeNotice ? <div className="work-context-mode-notice" role="status">{modeNotice}</div> : null}
      {modeError ? <div className="work-context-mode-error" role="alert">{modeError}</div> : null}
    </>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { data, bootstrapError, recoverWorkspace, refreshApplication, workspaceError } = useApp();
  const { contextSearch } = useWorkContext();
  const [refreshing, setRefreshing] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const calendar = useNativeCalendar();
  const sectionAccess = data.actor?.sectionAccess;
  const isCalendar = calendar.enabled && (location.pathname === "/calendar" || location.pathname.startsWith("/calendar/"));
  const operatorAccess = !calendar.enabled || calendar.bootstrap.operatorAccess;
  const calendarLeader = calendar.enabled && calendar.bootstrap.profile.role === "leader";
  // «Эфиры» — a section next to «Рассылки», no longer a calendar tab (Паша 30.09.2026); whoever sees the calendar sees
  // the webinars, the leader edits them and sees their analytics.
  const isWebinars = calendar.enabled && (location.pathname === "/webinars" || location.pathname.startsWith("/webinars/") || location.pathname.startsWith("/analytics/webinars"));
  const isLeadMagnets = location.pathname === "/lead-magnets" || location.pathname.startsWith("/lead-magnets/");
  const owner = canManageTeamRole(data.actor?.role);
  const navigation = SECTION_CATALOG.filter(section => {
    if (!section.navigation) return false;
    if (section.calendarOnly && !calendar.enabled) return false;
    if (sectionAccess) return sectionEntryPath(section.key, sectionAccess) !== null;
    switch (section.legacy) {
      case "operator": return operatorAccess;
      case "calendar": return calendar.enabled;
      case "calendar_leader": return calendarLeader;
      case "owner": return owner;
      case "owner_operator": return owner && operatorAccess;
      case "access": return owner || calendarLeader;
    }
  }).map(section => ({ category: section.category, to: sectionAccess ? sectionEntryPath(section.key, sectionAccess)! : section.path,
    label: section.navigationLabel, icon: sectionIcons[section.icon], activePaths: section.navigationPaths }));
  const activeTo = activeNavigation(navigation, location.pathname);
  const leadMagnetAccess = navigation.some(section => section.to === "/lead-magnets");
  const visibleSystemNavigation = navigation.filter(item => item.category === "Система");
  const isAnalytics = location.pathname === "/analytics" || location.pathname.startsWith("/analytics/");
  const isAnalyticsSection = isAnalytics && !location.pathname.startsWith("/analytics/webinars") && !location.pathname.startsWith("/analytics/campaigns");
  // The phone has one «Аналитика» in its bar: a menu of the sections this person may open («Входы» among them).
  const analyticsItems = navigation.filter(item => item.category === "Аналитика" || item.to === "/analytics/entries");
  const analyticsPaths = new Set(analyticsItems.map(item => item.to));
  const isDialogs = location.pathname.startsWith("/dialogs");
  const isPlayground = location.pathname === "/semantic-staging";
  // Campaigns are not scoped by the dialog agent/scenario, so the context
  // switcher (and its mode buttons) would only suggest a link that is not there.
  const isCampaigns = location.pathname === "/campaigns" || location.pathname.startsWith("/campaigns/") || location.pathname.startsWith("/analytics/campaigns");
  const isRobots = location.pathname === "/robots";
  const isPromises = location.pathname === "/promises";
  const isAlerts = location.pathname === "/alerts";
  const isBots = location.pathname === "/bots";
  const isWhere = location.pathname === "/where";
  // A report of an agent (04.11.2026): a page of its own, no agent or scenario to choose there.
  const isReport = location.pathname.startsWith("/report/");
  // «Спираль» (05.11.2026): «Маркетинг», a report and «Диалоги» carry their own head (title, time of data; the agent, scenario and
  // mode pills over the queue) — no row above it.
  // «Аналитика» in «Спираль» (05.11.2026): its sections have their own head — the name, the time of the data, the period.
  // «Спираль»: the calendar has its own heading with the date and its actions (the history among them) — no bar above it.
  const bareTop = isReport || isDialogs || location.pathname === "/analytics/marketing" || isAnalyticsSection || isCalendar;
  const actorName = data.actor?.name ?? "Владелец";
  const initials = actorName.split(" ").map((part) => part[0] ?? "").join("").slice(0, 2).toUpperCase();
  const systemActive = visibleSystemNavigation.some((item) => location.pathname.startsWith(item.to));

  async function recoverServerState(full: boolean) {
    setRefreshing(true);
    try {
      if (full) await refreshApplication();
      else await recoverWorkspaceSnapshot(recoverWorkspace);
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <div className={`app-shell app-shell--canonical${isDialogs ? " app-shell--dialogs" : ""} ${sidebarCollapsed ? "app-shell--sidebar-collapsed" : ""}${calendar.enabled ? " app-shell--native-calendar" : ""}${!operatorAccess ? " app-shell--calendar-only" : ""}`}>
      <aside className="sidebar">
        <Brand />
        {[...new Set(SECTION_CATALOG.map(section => section.category))].map(category =>
          <NavigationGroup key={category} label={category} items={navigation.filter(item => item.category === category)} search={contextSearch} collapsed={sidebarCollapsed} activeTo={activeTo} />
        )}
        <div className="sidebar__footer">
          <button type="button" className="sidebar-collapse" aria-label={sidebarCollapsed ? "Развернуть меню" : "Свернуть меню"} aria-expanded={!sidebarCollapsed} onClick={() => setSidebarCollapsed((value) => !value)}>
            <PanelLeftClose aria-hidden="true" size={17} />
            <span>Свернуть</span>
          </button>
          <NavLink to="/ai-agents" className={({ isActive }) => `sidebar-ai-agents${isActive ? " sidebar-ai-agents--active" : ""}`} title="ИИ-агенты: ваш Claude в Вехе">
            <Sparkles aria-hidden="true" size={17} />
            <span>ИИ-агенты</span>
          </NavLink>
          <AccountChip initials={initials} name={actorName} csrfToken={data.csrfToken}
            role={sectionAccess ? "Сотрудник · Академия гипнотерапии" : !operatorAccess && calendar.enabled ? `${calendar.bootstrap.profile.role === "manager" ? "Менеджер" : calendar.bootstrap.profile.role === "caller" ? "Лидоруб" : "Руководитель"} · Академия гипнотерапии` : data.actor?.role ? `${teamRoleLabel(data.actor.role)} · Академия гипнотерапии` : "Академия гипнотерапии"} />
        </div>
      </aside>

      {bareTop ? null : <header className="canonical-topbar">
        {import.meta.env.VITE_VEKHA_BETA === "true" ? <span className="beta-environment-label">Бета · тестовые данные</span> : null}
        {isLeadMagnets ? <div className="playground-topbar-label">Лид-магниты · время по Москве</div> : isAnalytics ? <div className="playground-topbar-label">Аналитика · время по Москве</div> : isWebinars ? <div className="playground-topbar-label webinars-topbar-label">Эфиры и серии · время по Москве</div> : isCalendar || (!operatorAccess && !sectionAccess) ? <div className="native-calendar-topbar"><CalendarDays size={19} /><strong>Календарь команды</strong><span>Все менеджеры · МСК</span><span className="native-calendar-scope-notice">Общее расписание</span><button type="button" className="calendar-icon-button" aria-label="История календаря" onClick={() => navigate("/calendar?notifications=1")}><History size={19} /></button></div> : <>{isPlayground ? <div className="playground-topbar-label">Личные тестовые диалоги</div> : isCampaigns ? <div className="playground-topbar-label campaigns-topbar-label">Рассылки · время по Москве</div> : isAlerts ? <div className="playground-topbar-label robots-topbar-label">Тревоги сервера · время по Москве</div> : isPromises ? <div className="playground-topbar-label robots-topbar-label">Обещания системы · время по Москве</div> : isRobots ? <div className="playground-topbar-label robots-topbar-label">Роботы вокруг amoCRM · время по Москве</div> : isBots ? <div className="playground-topbar-label robots-topbar-label">Боты Telegram · время по Москве</div> : isWhere ? <div className="playground-topbar-label">Справочник владельца · без агента</div> : isReport ? <div className="playground-topbar-label">Отчёт · время по Москве</div> : <WorkContextBar />}</>}
      </header>}

      <main className={isPlayground ? "content content--conversations content--playground" : isDialogs ? "content content--conversations" : "content"} id="main-content">
        {/* A section comes into focus when it opens (motion.css «Спирали»: экран наводится); moves inside a section do not replay it. */}
        <div key={location.pathname.split("/")[1] ?? ""} className={`${isPlayground ? "content__inner content__inner--conversations content__inner--playground" : isDialogs ? "content__inner content__inner--conversations" : "content__inner"} motion-screen`}>
          {workspaceError ? (
            <Notice tone="danger" title="Рабочие данные недоступны" actions={<Button variant="secondary" disabled={refreshing} onClick={() => void recoverServerState(false)}>{refreshing ? "Обновляем…" : "Перечитать базу"}</Button>}>
              {workspaceError} На экране остаётся последний подтверждённый снимок.
            </Notice>
          ) : null}
          {bootstrapError ? (
            <Notice tone="danger" title="Состояние приложения не обновилось" actions={<Button variant="secondary" disabled={refreshing} onClick={() => void recoverServerState(true)}>{refreshing ? "Обновляем…" : "Перечитать состояние"}</Button>}>
              {bootstrapError}
            </Notice>
          ) : null}
          {children}
        </div>
      </main>

      <nav className={`bottom-nav canonical-bottom-nav${sectionAccess ? " canonical-bottom-nav--sections" : ""}`} aria-label="Мобильная навигация">
        {/* «Сценарии» are not in the phone's bottom bar (Ден 13.10): a desktop tool, reachable from the menu. */}
        {sectionAccess ? <>{navigation.filter(item => item.to !== "/scenarios" && !analyticsPaths.has(item.to)).map(item => { const Icon=item.icon; const active=activeTo===item.to; return <NavLink key={item.to} to={item.to} aria-current={active ? "page" : undefined} className={`bottom-nav__item ${active ? "bottom-nav__item--active" : ""}`}><Icon aria-hidden="true" /><span>{item.label}</span></NavLink>; })}
          {analyticsItems.length ? <MobileAnalyticsNav items={analyticsItems} activeTo={activeTo} /> : null}</> : <>
        {calendar.enabled ? <NavLink to="/calendar" className={({ isActive }) => `bottom-nav__item ${isActive ? "bottom-nav__item--active" : ""}`}><CalendarDays aria-hidden="true" /><span>Календарь</span></NavLink> : null}
        {operatorAccess ? <NavLink to={{ pathname: "/dialogs", search: contextSearch }} className={({ isActive }) => `bottom-nav__item ${isActive ? "bottom-nav__item--active" : ""}`}><MessageSquare aria-hidden="true" /><span>Диалоги</span></NavLink> : null}
        {operatorAccess ? <NavLink to={{ pathname: "/campaigns", search: contextSearch }} className={() => `bottom-nav__item ${isCampaigns ? "bottom-nav__item--active" : ""}`} aria-current={isCampaigns ? "page" : undefined}><Megaphone aria-hidden="true" /><span>Рассылки</span></NavLink> : null}
        {leadMagnetAccess ? <MobileMaterialsNav isLeadMagnets={isLeadMagnets} isWebinars={isWebinars}/> : calendar.enabled ? <NavLink to="/webinars" className={() => `bottom-nav__item ${isWebinars ? "bottom-nav__item--active" : ""}`}><Radio aria-hidden="true" /><span>Эфиры</span></NavLink> : null}
        {analyticsItems.length ? <MobileAnalyticsNav items={analyticsItems} activeTo={activeTo} /> : null}
        {operatorAccess ? <><NavLink to={{ pathname: "/agents", search: contextSearch }} className={({ isActive }) => `bottom-nav__item ${isActive ? "bottom-nav__item--active" : ""}`}><Bot aria-hidden="true" /><span>Агенты</span></NavLink>
        <NavLink to={{ pathname: "/connections", search: contextSearch }} className={() => `bottom-nav__item ${systemActive ? "bottom-nav__item--active" : ""}`}><Settings2 aria-hidden="true" /><span>Система</span></NavLink></> : calendarLeader ? <NavLink to="/access" className={({ isActive }) => `bottom-nav__item ${isActive ? "bottom-nav__item--active" : ""}`}><UserRoundCheck aria-hidden="true" /><span>Доступ</span></NavLink> : null}
        </>}
      </nav>
    </div>
  );
}
/** The account at the foot of the menu: opens what concerns the person's own sign-in — the linked Telegram. */
function AccountChip({ initials, name, role, csrfToken }: { initials: string; name: string; role: string; csrfToken: string | null }) {
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState<TelegramLink | null | undefined>(undefined);
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    setFailed(null);
    void fetchOwnTelegramLink().then(setLink, () => setLink(null));
    const outside = (event: PointerEvent) => { if (event.target instanceof Node && !ref.current?.contains(event.target)) setOpen(false); };
    const escape = (event: globalThis.KeyboardEvent) => { if (event.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", outside); document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("pointerdown", outside); document.removeEventListener("keydown", escape); };
  }, [open]);
  async function unlink() {
    setPending(true);
    try { await unlinkOwnTelegram(csrfToken ?? ""); setLink(null); }
    catch (caught) { setFailed(caught instanceof Error ? caught.message : "Не получилось отвязать."); }
    finally { setPending(false); }
  }
  return <div className="account-chip-wrap" ref={ref}>
    {open ? <div className="account-menu" role="menu" aria-label="Учётная запись">
      {link === undefined ? <span className="account-menu__status">Telegram…</span> : link ? <>
        <span className="account-menu__status"><Send aria-hidden="true" size={16} />Telegram привязан</span>
        <button type="button" role="menuitem" disabled={pending} onClick={() => void unlink()}><Unlink aria-hidden="true" size={16} />Отвязать Telegram</button>
      </> : <span className="account-menu__status"><Send aria-hidden="true" size={16} />Telegram не привязан</span>}
      {failed ? <span className="account-menu__error" role="alert">{failed}</span> : null}
    </div> : null}
    <button type="button" className="account-chip" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(value => !value)}>
      <span className="avatar" aria-hidden="true">{initials}</span>
      <span>
        <strong>{name}</strong>
        <small>{role}</small>
      </span>
    </button>
  </div>;
}
/** «Аналитика» on the phone: one place in the bar, its sections in a menu; active on any of them. */
function MobileAnalyticsNav({ items, activeTo }: { items: readonly NavigationItem[]; activeTo: string | null }) {
  const [open, setOpen] = useState(false), ref = useRef<HTMLDivElement>(null);
  const location = useLocation();
  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => { if (event.target instanceof Node && !ref.current?.contains(event.target)) setOpen(false); };
    const escape = (event: globalThis.KeyboardEvent) => { if (event.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", outside); document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("pointerdown", outside); document.removeEventListener("keydown", escape); };
  }, [open]);
  const current = items.find(item => item.to === activeTo) ?? null;
  return <div className="mobile-materials-nav mobile-analytics-nav" ref={ref}>
    {open ? <div className="mobile-materials-menu" role="menu" aria-label="Аналитика">
      {items.map(item => { const Icon = item.icon; return <NavLink key={item.to} role="menuitem" to={item.to} end className={() => item.to === activeTo ? "is-active" : ""}><Icon size={18} />{item.label}</NavLink>; })}
    </div> : null}
    <button type="button" className={`bottom-nav__item ${current ? "bottom-nav__item--active" : ""}`} aria-label={current ? `Аналитика: ${current.label}` : "Аналитика"} aria-haspopup="menu"
      aria-expanded={open} onClick={() => setOpen(value => !value)}>
      <ChartNoAxesCombined aria-hidden="true" /><span>Аналитика</span>
    </button>
  </div>;
}
function MobileMaterialsNav({isLeadMagnets,isWebinars}:{isLeadMagnets:boolean;isWebinars:boolean}) {
  const [open,setOpen]=useState(false),ref=useRef<HTMLDivElement>(null);
  const location=useLocation();
  useEffect(()=>{setOpen(false);},[location.pathname]);
  useEffect(()=>{
    if(!open)return;
    const outside=(event:PointerEvent)=>{if(event.target instanceof Node&&!ref.current?.contains(event.target))setOpen(false);};
    const escape=(event:globalThis.KeyboardEvent)=>{if(event.key==='Escape')setOpen(false);};
    document.addEventListener('pointerdown',outside);document.addEventListener('keydown',escape);
    return()=>{document.removeEventListener('pointerdown',outside);document.removeEventListener('keydown',escape);};
  },[open]);
  return <div className="mobile-materials-nav" ref={ref}>
    {open?<div className="mobile-materials-menu" role="menu" aria-label="Материалы">
      <NavLink role="menuitem" to="/webinars"><Radio size={18}/>Эфиры</NavLink>
      <NavLink role="menuitem" to="/lead-magnets"><Gift size={18}/>Лид-магниты</NavLink>
    </div>:null}
    <button type="button" className={`bottom-nav__item ${isLeadMagnets||isWebinars?'bottom-nav__item--active':''}`}
      aria-label="Эфиры и лид-магниты" aria-haspopup="menu" aria-expanded={open} onClick={()=>setOpen(value=>!value)}>
      {isLeadMagnets?<Gift aria-hidden="true"/>:<Radio aria-hidden="true"/>}<span>{isLeadMagnets?'Материалы':'Эфиры'}</span>
    </button>
  </div>;
}
